import { memo, useMemo, useRef } from 'react';
import styled from 'styled-components';

type UseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
};
export const useModal = ({ isOpen, onClose, title }: UseModalProps) => {
  const isOpenRef = useRef(isOpen);
  const onCloseRef = useRef(onClose);
  const titleRef = useRef(title);

  isOpenRef.current = isOpen;
  onCloseRef.current = onClose;
  titleRef.current = title;

  const ModalComponent = useMemo(() => {
    const Component = ({ children }: { children: React.ReactNode }) => (
      <ModalBackground isOpen={isOpenRef.current} onClick={() => onCloseRef.current()}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <Title>{titleRef.current}</Title>
          {children}
        </ModalContainer>
      </ModalBackground>
    );

    return memo(Component);
  }, []);

  return { ModalComponent };
};

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.medium};
  margin-bottom: 0.5rem;
`;

const ModalBackground = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transition: opacity 0.3s;
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1rem;
  width: 30rem;
  max-height: 90vh;
  min-height: fit-content;
  height: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem;

  @media (max-width: 850px) {
    width: 90%;
    max-width: 30rem;
  }
`;
